import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { PageHeader, Card, Button, Input, Label, EmptyState } from "../components/ui-kit";
import { Wallet, Receipt, Camera, Send, FileDown } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLE = {
  pending: "bg-amber-100 text-amber-800",
  approved: "bg-emerald-100 text-emerald-800",
  rejected: "bg-red-100 text-red-800",
  "": "bg-[#E5E2DC] text-[#5C6B5E]",
};

const STATUS_LABEL = { pending: "Pending", approved: "Approved", rejected: "Rejected", "": "Not submitted" };

export default function MyExpensesPage() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => setRows((await api.get("/expenses/mine")).data || []);
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const downloadPdf = async (sid, dateKey) => {
    try {
      const res = await api.get(`/expenses/${sid}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `expense_${dateKey || sid}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  const totals = rows.reduce(
    (acc, r) => {
      const status = r.expense_status || "";
      acc.count += 1;
      if (status === "approved") acc.approved += Number(r.expense_total || 0);
      if (status === "pending") acc.pending += Number(r.expense_total || 0);
      if (status === "rejected") acc.rejected += Number(r.expense_total || 0);
      return acc;
    },
    { count: 0, approved: 0, pending: 0, rejected: 0 }
  );

  return (
    <div>
      <PageHeader title="My Expenses" subtitle="Track and submit your field expense claims per day" />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <SummaryTile icon={Receipt} label="Field days" value={totals.count} />
        <SummaryTile label="Pending" value={`₹${totals.pending.toFixed(0)}`} tone="pending" />
        <SummaryTile label="Approved" value={`₹${totals.approved.toFixed(0)}`} tone="approved" />
        <SummaryTile label="Rejected" value={`₹${totals.rejected.toFixed(0)}`} tone="rejected" />
      </div>

      <Card className="p-0 overflow-hidden">
        {rows.length === 0 ? (
          <div className="p-8">
            <EmptyState title="No field days yet" description="Complete a field day to submit an expense claim." />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-[#F9F8F6] border-b border-[#E5E2DC]">
                <tr className="text-left text-[11px] uppercase tracking-widest text-[#5C6B5E]">
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">KM</th>
                  <th className="px-4 py-3">Travel ₹</th>
                  <th className="px-4 py-3">DA</th>
                  <th className="px-4 py-3">Night stay</th>
                  <th className="px-4 py-3">Other</th>
                  <th className="px-4 py-3">Total</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-[#E5E2DC]" data-testid={`expense-row-${r.id}`}>
                    <td className="px-4 py-3 font-semibold">{r.date_key}</td>
                    <td className="px-4 py-3">{r.km_covered ?? 0}</td>
                    <td className="px-4 py-3">₹{Number(r.travel_amount || 0).toFixed(0)}</td>
                    <td className="px-4 py-3">₹{Number(r.daily_allowance || 0).toFixed(0)}</td>
                    <td className="px-4 py-3">₹{Number(r.night_stay_amount || 0).toFixed(0)}</td>
                    <td className="px-4 py-3">₹{Number(r.other_amount || 0).toFixed(0)}</td>
                    <td className="px-4 py-3 font-bold text-[#1E3F20]">₹{Number(r.expense_total || 0).toFixed(0)}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded ${STATUS_STYLE[r.expense_status || ""]}`}>
                        {STATUS_LABEL[r.expense_status || ""]}
                      </span>
                      {r.expense_status === "rejected" && r.expense_review_remarks && (
                        <div className="text-[11px] text-red-700 mt-1">Reason: {r.expense_review_remarks}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1">
                        {r.expense_status !== "approved" && (
                          <Button variant="outline" onClick={() => setEditing(r)} data-testid={`edit-expense-${r.id}`}>
                            {r.expense_status ? "Edit / Resubmit" : "Add Expenses"}
                          </Button>
                        )}
                        {r.expense_status && (
                          <button
                            onClick={() => downloadPdf(r.id, r.date_key)}
                            className="text-xs px-2.5 py-1 rounded bg-[#1E3F20]/10 text-[#1E3F20] hover:bg-[#1E3F20]/20 font-semibold inline-flex items-center gap-1"
                            data-testid={`pdf-expense-${r.id}`}
                          >
                            <FileDown className="w-3.5 h-3.5" /> PDF
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {editing && (
        <ExpenseModal
          session={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function SummaryTile({ icon: Icon, label, value, tone }) {
  const toneCls = tone === "approved" ? "text-emerald-700" : tone === "rejected" ? "text-red-700" : tone === "pending" ? "text-amber-700" : "text-[#1E3F20]";
  return (
    <Card>
      <div className="flex items-center gap-3">
        {Icon && <Icon className="w-5 h-5 text-[#1E3F20]" />}
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#5C6B5E] font-bold">{label}</div>
          <div className={`text-2xl font-heading font-black ${toneCls}`}>{value}</div>
        </div>
      </div>
    </Card>
  );
}

function ExpenseModal({ session, onClose, onSaved }) {
  const [dailyAllowance, setDailyAllowance] = useState(String(session.daily_allowance || ""));
  const [nightStay, setNightStay] = useState(String(session.night_stay_amount || ""));
  const [invoicePath, setInvoicePath] = useState(session.night_stay_invoice_path || "");
  const [invoiceName, setInvoiceName] = useState(session.night_stay_invoice_filename || "");
  const [otherAmt, setOtherAmt] = useState(String(session.other_amount || ""));
  const [otherRemarks, setOtherRemarks] = useState(session.other_remarks || "");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const rate = Number(session.rate_per_km || 0);
  const km = Number(session.km_covered || 0);
  const travel = km * rate;
  const total = travel + (Number(dailyAllowance) || 0) + (Number(nightStay) || 0) + (Number(otherAmt) || 0);

  const uploadInvoice = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/expenses/upload-invoice", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setInvoicePath(data.path);
      setInvoiceName(data.filename || file.name);
      toast.success("Invoice uploaded");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    setBusy(true);
    try {
      await api.put(`/daily-report/sessions/${session.id}/expenses`, {
        daily_allowance: Number(dailyAllowance) || 0,
        night_stay_amount: Number(nightStay) || 0,
        night_stay_invoice_path: invoicePath,
        night_stay_invoice_filename: invoiceName,
        other_amount: Number(otherAmt) || 0,
        other_remarks: otherRemarks,
      });
      toast.success("Expenses submitted for approval");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to submit");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 grid place-items-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-lg bg-white rounded-xl shadow-lg" onClick={(e) => e.stopPropagation()} data-testid="expense-modal">
        <div className="p-5 border-b border-[#E5E2DC] flex items-center justify-between">
          <div className="font-heading text-xl font-bold flex items-center gap-2">
            <Wallet className="w-5 h-5 text-[#1E3F20]" /> Expenses — {session.date_key}
          </div>
          <button onClick={onClose} className="text-[#5C6B5E]">×</button>
        </div>
        <div className="p-5 space-y-4">
          <div className="bg-[#F9F8F6] border border-[#E5E2DC] rounded-lg p-3 text-sm">
            <div className="flex justify-between"><span>Total KM (auto)</span><span className="font-mono">{km}</span></div>
            <div className="flex justify-between"><span>Rate per km</span><span className="font-mono">₹{rate.toFixed(2)}</span></div>
            <div className="flex justify-between font-bold text-[#1E3F20]"><span>Travel (auto)</span><span className="font-mono">₹{travel.toFixed(2)}</span></div>
            {rate === 0 && <div className="text-xs text-amber-700 mt-1">Rate per km is 0. Ask admin to set it on your user profile.</div>}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Daily allowance (₹)</Label>
              <Input type="number" step="0.01" min="0" value={dailyAllowance} onChange={(e) => setDailyAllowance(e.target.value)} data-testid="expense-daily" />
            </div>
            <div>
            <Label>Night stay (₹)</Label>
              <Input type="number" step="0.01" min="0" value={nightStay} onChange={(e) => setNightStay(e.target.value)} data-testid="expense-night" />
            </div>
          </div>

          <div>
            <Label>Any invoice to Upload (optional)</Label>
            <label className="inline-flex items-center gap-2 cursor-pointer bg-[#1E3F20]/10 text-[#1E3F20] hover:bg-[#1E3F20]/20 rounded-lg px-4 py-2 font-semibold text-sm">
              <Camera className="w-4 h-4" /> {invoiceName ? "Replace invoice" : "Attach invoice"}
              <input
                type="file"
                accept="image/*,application/pdf"
                className="hidden"
                data-testid="expense-invoice"
                onChange={(e) => uploadInvoice(e.target.files?.[0])}
              />
            </label>
            {uploading && <span className="text-xs text-[#5C6B5E] ml-2">Uploading…</span>}
            {invoiceName && <div className="text-xs text-[#5C6B5E] mt-1">{invoiceName}</div>}
          </div>

          <div>
            <Label>Other expenses (₹)</Label>
            <Input type="number" step="0.01" min="0" value={otherAmt} onChange={(e) => setOtherAmt(e.target.value)} data-testid="expense-other" />
          </div>
          <div>
            <Label>Other remarks</Label>
            <textarea
              rows={2}
              value={otherRemarks}
              onChange={(e) => setOtherRemarks(e.target.value)}
              data-testid="expense-other-remarks"
              className="w-full rounded-lg border border-[#E5E2DC] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3F20]/40"
            />
          </div>

          <div className="text-right text-sm">
            Grand total: <b className="font-heading text-2xl text-[#1E3F20]" data-testid="expense-total">₹{total.toFixed(2)}</b>
          </div>
        </div>
        <div className="p-5 border-t border-[#E5E2DC] flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || uploading} data-testid="expense-submit">
            <Send className="w-4 h-4" /> {busy ? "Submitting…" : "Submit for approval"}
          </Button>
        </div>
      </div>
    </div>
  );
}
